import { getLocalizedStringValue } from '@griply/utils';
import { useGoalMetricDayProgress, useGoals } from 'features/goals';
import React, { useMemo } from 'react';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useNumberFormat } from 'shared/hooks/use-number-format';
import { useTimeframeDifference } from 'shared/hooks/use-timeframe-difference';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { metricUnitFormatBase } from 'shared/types/metric-template';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';

import { InsightsCard } from './insights-card';
import { GoalInsightsCardProps } from './types';

export const GoalInsightsMetricEntriesCard: React.FC<GoalInsightsCardProps> = ({
  goal,
  startDate,
  endDate,
  ...rest
}) => {
  const locale = useI18nLocale();
  const goals = useGoals();
  const numberFormat = useNumberFormat();

  const goalProgress = useGoalMetricDayProgress(goal);
  const timeframeDifference = useTimeframeDifference({
    entries: goalProgress,
    startDate,
    endDate,
    startValue: goal.metric?.startValue ?? undefined,
    target: goal.metric?.targetValue ?? undefined,
  });

  const taskCounts = useTaskCounts();

  const completionPercentage = useMemo(
    () =>
      getCompletionPercentage(
        getGoalCompletion(goal, {
          goals,
          goalProgressCounts: taskCounts?.byGoalProgress,
        }),
      ),
    [goal, goals, taskCounts?.byGoalProgress],
  );

  const unitFormat = goal?.metric?.unit
    ? getLocalizedStringValue(
        goal.metric.unit.format ??
          `${metricUnitFormatBase} ${getLocalizedStringValue(goal.metric.unit.name, locale)}`,
        locale,
      )
    : undefined;

  return (
    <InsightsCard
      goal={goal}
      startDate={startDate}
      endDate={endDate}
      {...rest}
      entries={goalProgress}
      completionPercentage={completionPercentage}
      unitFormat={unitFormat}
      startValue={goal.metric?.startValue ?? undefined}
      target={goal.metric?.targetValue ?? undefined}
      timeframeDifference={timeframeDifference}
      numberFormat={numberFormat}
    />
  );
};
